/**
 * PasswordResetPage — two-state component:
 * 1. Request reset: email input → sends reset email
 * 2. Set new password: when URL has recovery token → password input
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SplitAuthLayout } from '@/layouts/SplitAuthLayout';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/services/supabaseClient';

/**
 * Did this page load as the return trip from a reset email?
 *
 * Read synchronously at first render — `detectSessionInUrl` consumes and
 * strips these parameters shortly after load, so a later check sees a clean
 * URL and wrongly concludes the visitor arrived cold.
 *
 * All three shapes are accepted, because which one arrives depends on the
 * configured flow and the email template:
 *
 *   ?code=...                        PKCE (what this client uses today)
 *   ?token_hash=...&type=recovery    verifyOtp-style template
 *   #access_token=...&type=recovery  legacy implicit flow
 *
 * The original check looked only for `?type=recovery`, which PKCE never
 * sends.
 */
export function arrivedFromRecoveryLink(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
  hash: string = typeof window === 'undefined' ? '' : window.location.hash,
): boolean {
  const query = new URLSearchParams(search);
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  return Boolean(
    query.get('code') ||
      query.get('token_hash') ||
      query.get('type') === 'recovery' ||
      fragment.get('access_token') ||
      fragment.get('type') === 'recovery',
  );
}

/**
 * Did the request fail because OUR side is broken, rather than because of
 * anything about this particular address?
 *
 * Only 5xx and transport failures qualify. Those are identical for every
 * address, so reporting them cannot help an attacker learn who has an account.
 */
function isServerFault(err: { status?: number; message?: string }): boolean {
  if (typeof err.status === 'number') {
    return err.status >= 500;
  }
  // No status at all means the request never got an HTTP answer.
  return /network|fetch|failed to fetch/i.test(err.message ?? '');
}

export default function PasswordResetPage() {
  // Decided once, from the URL as it was on arrival.
  const [isRecovery, setIsRecovery] = useState(() => arrivedFromRecoveryLink());

  useEffect(() => {
    // A hint only. The event is not required to establish the session and its
    // ordering is not guaranteed, so it can promote us into the recovery
    // screen but never out of it.
    //
    // Deliberately synchronous: awaiting a Supabase call inside this callback
    // holds an exclusive lock and deadlocks the client (auth-js#762).
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  if (isRecovery) {
    return <SetNewPassword />;
  }
  return <RequestReset />;
}

// ---------------------------------------------------------------------------
// Request reset
// ---------------------------------------------------------------------------

function RequestReset() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const { error: err } = await resetPassword(email);
      if (err && isServerFault(err)) {
        // Our side is broken, not theirs. This response is identical for
        // every address, so saying so reveals nothing about who is
        // registered — and claiming success here would leave someone waiting
        // for an email that was never sent.
        console.warn('[password-reset] send failed:', err.message);
        setError(
          "We couldn't send the reset email just now. Please try again in a " +
            'few minutes.',
        );
        return;
      }
      if (err) {
        // Everything else stays hidden. Supabase deliberately returns no
        // error for an unknown address, and the 4xx errors that DO occur are
        // rate-limit and account-state messages ("you can only request this
        // after N seconds") that only a REGISTERED address can provoke —
        // showing them would undo the protection.
        console.warn('[password-reset] request rejected:', err.message);
      }
      // Same confirmation whether or not the address exists.
      setSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <SplitAuthLayout>
        <div className="space-y-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-light">
            <svg className="h-6 w-6 text-teal-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Check your email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              If an account exists for <span className="font-medium text-foreground">{email}</span>,
              we've sent a password reset link.
            </p>
          </div>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              Back to sign in
            </Button>
          </Link>
        </div>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Reset your password</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              id="reset-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full bg-coral hover:bg-coral-hover" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Remember your password?{' '}
          <Link to="/login" className="text-coral hover:text-coral-hover font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </SplitAuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Set new password (after clicking reset link)
// ---------------------------------------------------------------------------

function SetNewPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        // The recovery link's only job is to establish a session; without one
        // updateUser fails with "Auth session missing!", which tells the
        // visitor nothing. This is a state real users reach: these links are
        // single-use, and corporate mail scanners routinely prefetch and burn
        // them before the human ever clicks.
        const linkNoLongerValid = /session|expired|invalid|not found/i.test(
          err.message,
        );
        setError(
          linkNoLongerValid
            ? 'This reset link has expired or has already been used. Request a new one below.'
            : err.message,
        );
      } else {
        setSuccess(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <SplitAuthLayout>
        <div className="space-y-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-light">
            <svg className="h-6 w-6 text-teal-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Password updated</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your password has been successfully reset.
            </p>
          </div>
          <Link to="/login">
            <Button className="w-full bg-coral hover:bg-coral-hover">
              Sign in with new password
            </Button>
          </Link>
        </div>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Set new password</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose a strong password for your account
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <PasswordStrengthIndicator password={password} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-new-password">Confirm new password</Label>
            <Input
              id="confirm-new-password"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full bg-coral hover:bg-coral-hover" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update password'}
          </Button>
        </form>
      </div>
    </SplitAuthLayout>
  );
}
